import React, { memo, useCallback, useEffect } from 'react';
import Clear from '@mui/icons-material/Clear';
import Backspace from '@mui/icons-material/Backspace';
import { Grid, Divider, Box } from '@mui/material';

type PinNumpadProps = {
  onPinDigitEntered: (digit: string) => void;
  onPinDigitDeleted: (action?: 'all') => void;
};

const buttonStyles = {
  height: 48,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
};

const PinNumpad: React.FC<PinNumpadProps> = (props) => {
  const { onPinDigitEntered, onPinDigitDeleted } = props;

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key;

      if (/^[0-9]$/.test(key)) {
        onPinDigitEntered(key);
        return;
      }

      if (key === 'Backspace') {
        onPinDigitDeleted();
      }
    },
    [onPinDigitEntered, onPinDigitDeleted],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  const renderButton = useCallback(
    (digit: string) => (
      <Box sx={buttonStyles} onClick={() => onPinDigitEntered(digit)}>
        {digit}
      </Box>
    ),
    [onPinDigitEntered],
  );

  const handleBackspace = useCallback(() => {
    onPinDigitDeleted();
  }, [onPinDigitDeleted]);

  const handleClear = useCallback(() => {
    onPinDigitDeleted('all');
  }, [onPinDigitDeleted]);

  const isPortrait = window.innerHeight > window.innerWidth;

  return (
    <Box sx={{ width: '100%', maxWidth: '380px' }}>
      {isPortrait && <Divider sx={{ mb: 4 }} />}
      <Grid container>
        <Grid item xs={4}>
          {renderButton('1')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('2')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('3')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('4')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('5')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('6')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('7')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('8')}
        </Grid>
        <Grid item xs={4}>
          {renderButton('9')}
        </Grid>
        <Grid item xs={4}>
          <Box sx={buttonStyles} onClick={handleBackspace}>
            <Backspace />
          </Box>
        </Grid>
        <Grid item xs={4}>
          {renderButton('0')}
        </Grid>
        <Grid item xs={4}>
          <Box sx={buttonStyles} onClick={handleClear}>
            <Clear />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

PinNumpad.displayName = 'PinNumpad';

export default memo(PinNumpad);
